import type {
  MediaItem,
  BlockBase,
  TextBlock,
  QuoteBlock,
  TextImageBlock,
  TextCarouselBlock,
  ImageBlock,
  TwoImagesBlock,
  ImageGridBlock,
  ImageGridTextBlock,
  ImageCarouselBlock,
} from "../../../types"

function looksLikeVideo(url?: string) {
  const u = String(url || "").toLowerCase()
  return (
    u.endsWith(".mp4") ||
    u.endsWith(".webm") ||
    u.endsWith(".ogg") ||
    u.endsWith(".mov") ||
    u.endsWith(".m4v") ||
    u.includes("/video")
  )
}

export function MediaThumb({
  thumbnail,
  title,
  fit = "fit",
  width = 220,
  height = 140,
  position = "center center",
}: {
  thumbnail?: string
  title?: string
  fit?: "fit" | "cover"
  width?: number
  height?: number
  position?: string
}) {
  const isVideo = looksLikeVideo(thumbnail)

  return (
    <div
      style={{
        width,
        height,
        border: "1px solid rgba(0,0,0,.1)",
        borderRadius: 10,
        overflow: "hidden",
        background: "linear-gradient(135deg, rgba(0,0,0,.05), rgba(0,0,0,.02))",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
      }}
    >
      {thumbnail ? (
        isVideo ? (
          <>
            <video
              src={String(thumbnail)}
              muted
              playsInline
              preload="metadata"
              style={{
                width: "100%",
                height: "100%",
                objectFit: fit === "cover" ? "cover" : "contain",
                display: "block",
              }}
            />
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "linear-gradient(to top, rgba(0,0,0,.20), rgba(0,0,0,0))",
                pointerEvents: "none",
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 999,
                  background: "rgba(255,255,255,.92)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 10px 26px rgba(0,0,0,.22)",
                }}
              >
                <div
                  style={{
                    width: 0,
                    height: 0,
                    borderTop: "7px solid transparent",
                    borderBottom: "7px solid transparent",
                    borderLeft: "10px solid rgba(0,0,0,.65)",
                    marginLeft: 2,
                  }}
                />
              </div>
            </div>
          </>
        ) : (
          <img
            src={String(thumbnail)}
            alt={title || "Immagine"}
            style={{
              width: "100%",
              height: "100%",
              objectFit: fit === "cover" ? "cover" : "contain",
              objectPosition: position || "center center",
              display: "block",
            }}
          />
        )
      ) : (
        <span className="text-muted" style={{ fontSize: 13 }}>
          Nessuna anteprima
        </span>
      )}
    </div>
  )
}

function Chip({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: "rgba(0,0,0,.55)" }}>{label}</span>
      <span style={{ fontSize: 12.5, color: "rgba(0,0,0,.85)" }}>{value}</span>
    </div>
  )
}

// ============ BLOCK PREVIEWS ============

function TextBlockPreview({ block }: { block: TextBlock }) {
  return (
    <div
      style={{
        border: "1px solid rgba(0,0,0,.08)",
        background: "#fff",
        borderRadius: 14,
        padding: 16,
        boxShadow: "0 6px 22px rgba(0,0,0,.04)",
      }}
    >
      <div className="mb-2" style={{ fontSize: 12, fontWeight: 700, color: "rgba(0,0,0,.55)" }}>
        Titolo slide: <span style={{ color: "rgba(0,0,0,.85)", fontWeight: 600 }}>{block.slideTitle || "—"}</span>
      </div>
      <h5 style={{ marginBottom: 8 }}>{block.title || "—"}</h5>
      <div
        style={{ fontSize: 14, color: "rgba(0,0,0,.75)" }}
        dangerouslySetInnerHTML={{ __html: block.content || "" }}
      />
      {block.color && (
        <div className="mt-3">
          <Chip
            label="Colore sfondo"
            value={
              <span style={{ backgroundColor: block.color, padding: "2px 8px", borderRadius: 4 }}>{block.color}</span>
            }
          />
        </div>
      )}
    </div>
  )
}

function QuoteBlockPreview({ block }: { block: QuoteBlock }) {
  return (
    <div
      style={{
        border: "1px solid rgba(0,0,0,.08)",
        background: "#fff",
        borderRadius: 14,
        padding: 16,
        boxShadow: "0 6px 22px rgba(0,0,0,.04)",
        borderLeft: "4px solid #0d6efd",
      }}
    >
      <div className="mb-2" style={{ fontSize: 12, fontWeight: 700, color: "rgba(0,0,0,.55)" }}>
        Titolo slide: <span style={{ color: "rgba(0,0,0,.85)", fontWeight: 600 }}>{block.slideTitle || "—"}</span>
      </div>
      <blockquote style={{ margin: 0, fontStyle: "italic", fontSize: 16, color: "rgba(0,0,0,.8)" }}>
        {block.quote || "—"}
      </blockquote>
      <div className="mt-2" style={{ fontSize: 14, fontWeight: 600, color: "rgba(0,0,0,.7)" }}>
        — {block.author || "—"}
      </div>
    </div>
  )
}

function TextImageBlockPreview({ block }: { block: TextImageBlock }) {
  const isLeft = block.textPosition === "left"

  return (
    <div
      style={{
        border: "1px solid rgba(0,0,0,.08)",
        background: "#fff",
        borderRadius: 14,
        padding: 16,
        boxShadow: "0 6px 22px rgba(0,0,0,.04)",
      }}
    >
      <div className="mb-2" style={{ fontSize: 12, fontWeight: 700, color: "rgba(0,0,0,.55)" }}>
        Titolo slide: <span style={{ color: "rgba(0,0,0,.85)", fontWeight: 600 }}>{block.slideTitle || "—"}</span>
      </div>

      <div className="d-flex gap-3" style={{ flexDirection: isLeft ? "row" : "row-reverse" }}>
        <div style={{ flex: 1 }}>
          {block.title && <h5 style={{ marginBottom: 8 }}>{block.title}</h5>}
          <div
            style={{ fontSize: 14, color: "rgba(0,0,0,.75)" }}
            dangerouslySetInnerHTML={{ __html: block.content || "" }}
          />
        </div>

        <div style={{ flex: 1 }}>
          <MediaThumb
            thumbnail={block.mediaThumbnail}
            title={block.mediaTitle}
            fit={block.fit}
            width={200}
            height={140}
          />
          {block.caption && (
            <div className="mt-1 text-center" style={{ fontSize: 12, color: "rgba(0,0,0,.6)" }}>
              {block.caption}
            </div>
          )}
        </div>
      </div>

      <div className="mt-3 d-flex gap-3 flex-wrap">
        <Chip label="Posizione testo" value={block.textPosition === "left" ? "Sinistra" : "Destra"} />
        <Chip label="Fit" value={block.fit} />
      </div>
    </div>
  )
}

function TextCarouselPreview({ block }: { block: TextCarouselBlock }) {
  const isLeft = block.textPosition === "left"

  return (
    <div
      style={{
        border: "1px solid rgba(0,0,0,.08)",
        background: "#fff",
        borderRadius: 14,
        padding: 16,
        boxShadow: "0 6px 22px rgba(0,0,0,.04)",
      }}
    >
      <div className="mb-2" style={{ fontSize: 12, fontWeight: 700, color: "rgba(0,0,0,.55)" }}>
        Titolo slide: <span style={{ color: "rgba(0,0,0,.85)", fontWeight: 600 }}>{block.slideTitle || "—"}</span>
      </div>

      <div className="d-flex gap-3" style={{ flexDirection: isLeft ? "row" : "row-reverse" }}>
        <div style={{ flex: 1 }}>
          {block.title && <h5 style={{ marginBottom: 8 }}>{block.title}</h5>}
          <div
            style={{ fontSize: 14, color: "rgba(0,0,0,.75)" }}
            dangerouslySetInnerHTML={{ __html: block.content || "" }}
          />
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {block.items?.slice(0, 3).map((it, idx) => (
              <MediaThumb
                key={it.id ?? idx}
                thumbnail={it.thumbnail}
                title={it.title}
                fit={it.fit}
                width={100}
                height={70}
              />
            ))}
            {block.items?.length > 3 && (
              <div style={{ fontSize: 12, color: "rgba(0,0,0,.5)", alignSelf: "center" }}>
                +{block.items.length - 3} altre
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-3 d-flex gap-3 flex-wrap">
        <Chip label="Immagini" value={block.items?.length || 0} />
        <Chip label="Posizione testo" value={block.textPosition === "left" ? "Sinistra" : "Destra"} />
        <Chip label="Didascalia" value={block.captionMode === "single" ? "Unica" : "Per immagine"} />
      </div>
    </div>
  )
}

function ImageBlockPreview({ block }: { block: ImageBlock }) {
  return (
    <div
      style={{
        border: "1px solid rgba(0,0,0,.08)",
        background: "#fff",
        borderRadius: 14,
        padding: 16,
        boxShadow: "0 6px 22px rgba(0,0,0,.04)",
      }}
    >
      <div className="mb-2" style={{ fontSize: 12, fontWeight: 700, color: "rgba(0,0,0,.55)" }}>
        Titolo slide: <span style={{ color: "rgba(0,0,0,.85)", fontWeight: 600 }}>{block.slideTitle || "—"}</span>
      </div>

      <div className="text-center">
        <MediaThumb
          thumbnail={block.mediaThumbnail}
          title={block.mediaTitle}
          fit={block.fit}
          width={300}
          height={200}
        />
        {block.caption && (
          <div className="mt-2" style={{ fontSize: 13, color: "rgba(0,0,0,.6)" }}>
            {block.caption}
          </div>
        )}
      </div>

      <div className="mt-3 d-flex gap-3 flex-wrap">
        <Chip label="Fit" value={block.fit} />
      </div>
    </div>
  )
}

function TwoImagesBlockPreview({ block }: { block: TwoImagesBlock }) {
  const items = block.items || []

  return (
    <div
      style={{
        border: "1px solid rgba(0,0,0,.08)",
        background: "#fff",
        borderRadius: 14,
        padding: 16,
        boxShadow: "0 6px 22px rgba(0,0,0,.04)",
      }}
    >
      <div className="mb-2" style={{ fontSize: 12, fontWeight: 700, color: "rgba(0,0,0,.55)" }}>
        Titolo slide: <span style={{ color: "rgba(0,0,0,.85)", fontWeight: 600 }}>{block.slideTitle || "—"}</span>
      </div>

      <div className="d-flex gap-3 justify-content-center">
        {[0, 1].map((i) => {
          const it = items[i]
          return (
            <div key={i} style={{ flex: 1, maxWidth: 200 }}>
              <MediaThumb thumbnail={it?.thumbnail} title={it?.title} fit={it?.fit} width={180} height={120} />
              {it?.caption && (
                <div className="mt-1 text-center" style={{ fontSize: 12, color: "rgba(0,0,0,.6)" }}>
                  {it.caption}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function ImageGridBlockPreview({ block }: { block: ImageGridBlock }) {
  const { rows, cols, items, captionMode, caption } = block

  return (
    <div
      style={{
        border: "1px solid rgba(0,0,0,.08)",
        background: "#fff",
        borderRadius: 14,
        padding: 16,
        boxShadow: "0 6px 22px rgba(0,0,0,.04)",
      }}
    >
      <div className="mb-2" style={{ fontSize: 12, fontWeight: 700, color: "rgba(0,0,0,.55)" }}>
        Titolo slide: <span style={{ color: "rgba(0,0,0,.85)", fontWeight: 600 }}>{block.slideTitle || "—"}</span>
      </div>

      <div className="mb-3 d-flex gap-3 flex-wrap">
        <Chip label="Righe" value={rows} />
        <Chip label="Colonne" value={cols} />
        <Chip label="Immagini" value={items?.length || 0} />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gap: 8,
        }}
      >
        {items?.map((it, idx) => (
          <div key={it.id ?? idx}>
            <MediaThumb thumbnail={it.thumbnail} title={it.title} fit={it.fit} width={100} height={70} />
            {captionMode === "perItem" && it.caption && (
              <div className="mt-1 text-center" style={{ fontSize: 10, color: "rgba(0,0,0,.6)" }}>
                {it.caption}
              </div>
            )}
          </div>
        ))}
      </div>

      {captionMode === "single" && caption && (
        <div className="mt-2" style={{ fontSize: 13, color: "rgba(0,0,0,.6)" }}>
          {caption}
        </div>
      )}
    </div>
  )
}

function ImageGridTextBlockPreview({ block }: { block: ImageGridTextBlock }) {
  const { rows, cols, items, captionMode, content, textPosition } = block
  const isLeft = textPosition === "left"

  return (
    <div
      style={{
        border: "1px solid rgba(0,0,0,.08)",
        background: "#fff",
        borderRadius: 14,
        padding: 16,
        boxShadow: "0 6px 22px rgba(0,0,0,.04)",
      }}
    >
      <div className="mb-2" style={{ fontSize: 12, fontWeight: 700, color: "rgba(0,0,0,.55)" }}>
        Titolo slide: <span style={{ color: "rgba(0,0,0,.85)", fontWeight: 600 }}>{block.slideTitle || "—"}</span>
      </div>

      <div className="d-flex gap-3" style={{ flexDirection: isLeft ? "row" : "row-reverse" }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, color: "rgba(0,0,0,.75)" }} dangerouslySetInnerHTML={{ __html: content || "" }} />
        </div>

        <div style={{ flex: 1 }}>
          <div className="mb-2 d-flex gap-2 flex-wrap">
            <Chip label="Righe" value={rows} />
            <Chip label="Colonne" value={cols} />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${cols}, 1fr)`,
              gap: 4,
            }}
          >
            {items?.slice(0, rows * cols).map((it, idx) => (
              <MediaThumb
                key={it.id ?? idx}
                thumbnail={it.thumbnail}
                title={it.title}
                fit={it.fit}
                width={60}
                height={45}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="mt-3 d-flex gap-3 flex-wrap">
        <Chip label="Posizione testo" value={textPosition === "left" ? "Sinistra" : "Destra"} />
        <Chip label="Didascalia" value={captionMode === "single" ? "Unica" : "Per immagine"} />
      </div>
    </div>
  )
}

function ImageCarouselBlockPreview({ block }: { block: ImageCarouselBlock }) {
  return (
    <div
      style={{
        border: "1px solid rgba(0,0,0,.08)",
        background: "#fff",
        borderRadius: 14,
        padding: 16,
        boxShadow: "0 6px 22px rgba(0,0,0,.04)",
      }}
    >
      <div className="mb-2" style={{ fontSize: 12, fontWeight: 700, color: "rgba(0,0,0,.55)" }}>
        Titolo slide: <span style={{ color: "rgba(0,0,0,.85)", fontWeight: 600 }}>{block.slideTitle || "—"}</span>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {block.items?.slice(0, 4).map((it, idx) => (
          <div key={it.id ?? idx}>
            <MediaThumb thumbnail={it.thumbnail} title={it.title} fit={it.fit} width={100} height={70} />
            {block.captionMode === "perItem" && it.caption && (
              <div className="mt-1 text-center" style={{ fontSize: 10, color: "rgba(0,0,0,.6)" }}>
                {it.caption}
              </div>
            )}
          </div>
        ))}
      </div>

      {block.captionMode === "single" && block.caption && (
        <div className="mt-2" style={{ fontSize: 13, color: "rgba(0,0,0,.6)" }}>
          {block.caption}
        </div>
      )}

      <div className="mt-3 d-flex gap-3 flex-wrap">
        <Chip label="Immagini" value={block.items?.length || 0} />
        <Chip label="Didascalia" value={block.captionMode === "single" ? "Unica" : "Per immagine"} />
      </div>
    </div>
  )
}

// ============ MAIN PREVIEW COMPONENT ============

export function BlockPreview({ block }: { block: BlockBase }) {
  switch (block.blockType) {
    case "Text":
      return <TextBlockPreview block={block as TextBlock} />
    case "Quote":
      return <QuoteBlockPreview block={block as QuoteBlock} />
    case "TextImage":
      return <TextImageBlockPreview block={block as TextImageBlock} />
    case "TextCarousel":
      return <TextCarouselPreview block={block as TextCarouselBlock} />
    case "Image":
      return <ImageBlockPreview block={block as ImageBlock} />
    case "TwoImages":
      return <TwoImagesBlockPreview block={block as TwoImagesBlock} />
    case "ImageGrid":
      return <ImageGridBlockPreview block={block as ImageGridBlock} />
    case "ImageGridText":
      return <ImageGridTextBlockPreview block={block as ImageGridTextBlock} />
    case "ImageCarousel":
      return <ImageCarouselBlockPreview block={block as ImageCarouselBlock} />
    default:
      return <div className="text-muted">Tipo di blocco non riconosciuto</div>
  }
}

// ============ COLLAPSED HINT ============

export function BlockCollapsedHint({ block }: { block: BlockBase }) {
  const getHint = () => {
    switch (block.blockType) {
      case "Text":
        return `Titolo: ${(block as TextBlock).title || "—"}`
      case "Quote":
        return `"${(block as QuoteBlock).quote?.substring(0, 50) || "—"}..."`
      case "TextImage":
        return `${(block as TextImageBlock).mediaId ? "1 immagine" : "Nessuna immagine"} | Posizione: ${(block as TextImageBlock).textPosition === "left" ? "Sinistra" : "Destra"}`
      case "TextCarousel":
        return `${(block as TextCarouselBlock).items?.length || 0} immagini | Posizione: ${(block as TextCarouselBlock).textPosition === "left" ? "Sinistra" : "Destra"}`
      case "Image":
        return (block as ImageBlock).mediaId ? "1 immagine selezionata" : "Nessuna immagine"
      case "TwoImages":
        return `${(block as TwoImagesBlock).items?.filter((i) => i.id).length || 0} / 2 immagini`
      case "ImageGrid":
        return `${(block as ImageGridBlock).rows}×${(block as ImageGridBlock).cols} | ${(block as ImageGridBlock).items?.length || 0} immagini`
      case "ImageGridText":
        return `${(block as ImageGridTextBlock).rows}×${(block as ImageGridTextBlock).cols} | ${(block as ImageGridTextBlock).items?.length || 0} immagini | Testo: ${(block as ImageGridTextBlock).textPosition === "left" ? "Sinistra" : "Destra"}`
      case "ImageCarousel":
        return `${(block as ImageCarouselBlock).items?.length || 0} immagini | Didascalia: ${(block as ImageCarouselBlock).captionMode === "single" ? "Unica" : "Per immagine"}`
      default:
        return "Configurazione non completata"
    }
  }

  const slideTitle = (block as any).slideTitle

  return (
    <span className="text-muted" style={{ fontSize: 13 }}>
      <strong style={{ color: "rgba(0,0,0,.75)" }}>{slideTitle || "Slide senza titolo"}</strong>
      {" · "}
      {getHint()}
    </span>
  )
}

// ============ LEGACY EXPORTS ============

export function CarouselPreviewGrid({
  items,
  captionMode,
  singleCaption,
}: {
  items: MediaItem[]
  captionMode: "single" | "perItem"
  singleCaption?: string
}) {
  return (
    <div className="d-flex flex-column gap-3">
      <div
        style={{
          border: "1px solid rgba(0,0,0,.08)",
          background: "#fff",
          borderRadius: 14,
          padding: 14,
          boxShadow: "0 6px 22px rgba(0,0,0,.04)",
          display: "flex",
          gap: 14,
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(0,0,0,.55)" }}>Immagini</div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>{items.length}</div>
          </div>

          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(0,0,0,.55)" }}>Didascalia</div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>{captionMode === "single" ? "Unica" : "Per media"}</div>
          </div>
        </div>

        {captionMode === "single" && (
          <div style={{ maxWidth: 520 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(0,0,0,.55)" }}>Didascalia unica</div>
            <div style={{ fontSize: 13.5, color: "rgba(0,0,0,.85)" }}>
              {singleCaption ? singleCaption : <span className="text-muted">nessuna</span>}
            </div>
          </div>
        )}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          gap: 16,
        }}
      >
        {items.map((it, idx) => {
          const w = 320
          const h = 200

          return (
            <div
              key={it.id ?? idx}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              <MediaThumb thumbnail={it.thumbnail} title={it.title} fit={it.fit ?? "fit"} width={w} height={h} />

              <div
                style={{
                  border: "1px solid rgba(0,0,0,.08)",
                  background: "#fff",
                  borderRadius: 14,
                  padding: 12,
                  boxShadow: "0 6px 22px rgba(0,0,0,.04)",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                  <Chip label="ID" value={it.id ?? <span className="text-muted">—</span>} />
                  <Chip label="Fit" value={it.fit ?? "fit"} />
                </div>

                {captionMode === "perItem" && (
                  <div style={{ marginTop: 10 }}>
                    <Chip
                      label="Didascalia"
                      value={it.caption ? it.caption : <span className="text-muted">nessuna</span>}
                    />
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
